import { useMemo } from "react";
import * as Sentry from "@sentry/browser";

import { AnyWizardDefinition, WizardMascot, WizardSession } from "./types";
import { useWizards } from "./WizardProvider";

export type WizardCommand = {
  title: string;
  label: string;
  searchKeys: string[];
  Icon?: WizardMascot["Art"];
  run: () => void;
};

function commandsFor(
  definition: AnyWizardDefinition,
  sessions: WizardSession[],
  openWizard: (wizardType: string) => void,
  expandWizard: (sessionId: string) => void,
): WizardCommand[] {
  const inProgress = sessions.filter(
    (session) =>
      session.type === definition.type && session.completedAt === null,
  );
  const keyed = inProgress.filter(
    (session) => definition.getSessionKey?.(session.data) !== undefined,
  );
  const hasDraft = inProgress.length > keyed.length;
  const baseSearchKeys = [
    "wizard",
    ...definition.title.toLowerCase().split(" "),
    ...definition.searchKeys.map((key) => key.toLowerCase()),
  ];
  const prefix = hasDraft ? "Resume wizard" : "Start wizard";

  return [
    {
      title: `${prefix} > ${definition.title}`,
      label: definition.description,
      searchKeys: [prefix.toLowerCase(), ...baseSearchKeys],
      Icon: definition.mascot?.Art,
      run: () => openWizard(definition.type),
    },
    ...keyed.map((session) => {
      const subtitle =
        definition.getSubtitle?.(session.data) ??
        definition.getSessionKey?.(session.data);
      return {
        title: `Resume wizard > ${subtitle}`,
        label: definition.title,
        searchKeys: [
          "resume wizard",
          ...baseSearchKeys,
          ...(subtitle?.toLowerCase().split(" ") ?? []),
        ],
        Icon: definition.mascot?.Art,
        run: () => expandWizard(session.id),
      };
    }),
  ];
}

export function useWizardCommands(): WizardCommand[] {
  const { definitions, state, openWizard, expandWizard } = useWizards();

  return useMemo(() => {
    const sessions = Object.values(state.sessions);
    // One broken definition must not take the whole command bar down with it.
    return Object.values(definitions).flatMap((definition) => {
      try {
        return commandsFor(definition, sessions, openWizard, expandWizard);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { area: "wizard", wizardType: definition.type },
        });
        return [];
      }
    });
  }, [definitions, state.sessions, openWizard, expandWizard]);
}
